import { PageHeader, Table, Input, message, Tag } from "antd";
import React, { Component } from "react";
import axios from "axios";
import { connect } from "react-redux";
import * as XLSX from "xlsx";
import { FileExcelOutlined, SearchOutlined } from "@ant-design/icons";
import {
  ButtonItem,
  round2Decimals,
  dineroDisabledMask,
} from "../../../../Components/Items";

class ExportarListas extends Component {
  state = {
    todasListas: [],
    loading: false,
    descargando: false,
    seleccionadas: [],
    busqueda: "",
  };

  componentDidMount() {
    this.consultarListas();
  }

  consultarListas = () => {
    this.setState({ loading: true });
    axios
      .post(
        "listasEscuela/selectTodasListasEscuela",
        { empresaId: this.props.empresaId, valor: 1, page: 1, pageSize: 100000 },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({ todasListas: res.data.data || [], loading: false });
        } else {
          this.setState({ todasListas: [], loading: false });
        }
      })
      .catch(() => {
        message.error("No se pudieron cargar las listas");
        this.setState({ todasListas: [], loading: false });
      });
  };

  /**
   * Trae, en una sola llamada, los datos de las listas seleccionadas ya con el
   * precio unitario, subtotal e impuestos calculados por el backend (respetando
   * la lógica de precios/impuestos de la empresa, igual que el storefront).
   *
   * ── Contrato del endpoint ──
   *   POST listasEscuela/datosListasParaExcel
   *   body: { empresaId, listaIds: [String] }
   *   resp: {
   *     success: 2,
   *     listas: [
   *       {
   *         _id, nombre,
   *         subtotal,                   // Σ subtotales (sin impuestos)
   *         impuestos,                  // Σ impuestos
   *         total,                      // subtotal + impuestos
   *         insumos: [
   *           {
   *             codigo, descripcion, cantidad,
   *             precioUnitario,         // PU sin impuestos
   *             subtotal,               // cantidad × PU
   *             impuestos,              // cantidad × impuesto/unidad
   *             total,                  // subtotal + impuestos
   *             requerido
   *           }
   *         ]
   *       }
   *     ]
   *   }
   */
  consultarDatosListas = async (listaIds) => {
    const res = await axios.post(
      "listasEscuela/datosListasParaExcel",
      { empresaId: this.props.empresaId, listaIds },
      { headers: this.props.headersToken }
    );
    if (res.data.success !== 2) return [];
    return res.data.listas || [];
  };

  // Excel no permite estos caracteres en nombre de hoja y máximo 31 caracteres.
  sanitizarNombreHoja = (nombre, usados) => {
    let base = String(nombre || "Lista")
      .replace(/[\\/?*[\]:]/g, " ")
      .trim()
      .slice(0, 31);
    if (!base) base = "Lista";
    let candidato = base;
    let n = 2;
    while (usados.has(candidato.toLowerCase())) {
      const sufijo = ` (${n})`;
      candidato = base.slice(0, 31 - sufijo.length) + sufijo;
      n += 1;
    }
    usados.add(candidato.toLowerCase());
    return candidato;
  };

  descargarExcel = async () => {
    const { seleccionadas, todasListas } = this.state;
    if (seleccionadas.length === 0) {
      message.info("Selecciona al menos una lista para exportar");
      return;
    }

    this.setState({ descargando: true });
    try {
      const listasMap = new Map(
        todasListas.map((l) => [String(l._id), l])
      );
      const datos = await this.consultarDatosListas(seleccionadas);

      if (datos.length === 0) {
        message.error("El servidor no devolvió datos de las listas");
        this.setState({ descargando: false });
        return;
      }

      const wb = XLSX.utils.book_new();
      const usados = new Set();
      const resumen = [];

      datos.forEach((data) => {
        const nombreLista =
          data?.nombre || listasMap.get(String(data?._id))?.nombre || "Lista";
        const insumos = data?.insumos || [];

        // Acumulados de la lista (con fallback a la suma de los insumos).
        const subtotalLista =
          data?.subtotal != null
            ? round2Decimals(Number(data.subtotal))
            : round2Decimals(
                insumos.reduce((acc, i) => acc + Number(i.subtotal || 0), 0)
              );
        const impuestosLista =
          data?.impuestos != null
            ? round2Decimals(Number(data.impuestos))
            : round2Decimals(
                insumos.reduce((acc, i) => acc + Number(i.impuestos || 0), 0)
              );
        const totalLista =
          data?.total != null
            ? round2Decimals(Number(data.total))
            : round2Decimals(subtotalLista + impuestosLista);
        const requeridos = insumos.filter((i) => i.requerido).length;

        // Hoja de la lista: una fila por insumo + fila de total.
        const filas = insumos.map((i) => {
          const cantidad = Number(i.cantidad || 0);
          const precioUnitario = Number(i.precioUnitario || 0);
          const subtotal =
            i.subtotal != null
              ? Number(i.subtotal)
              : round2Decimals(cantidad * precioUnitario);
          const impuestos = Number(i.impuestos || 0);
          const total =
            i.total != null ? Number(i.total) : round2Decimals(subtotal + impuestos);
          return {
            Código: i.codigo ?? "",
            Insumo: i.descripcion || "",
            Cantidad: cantidad,
            "Precio Unitario": round2Decimals(precioUnitario),
            Subtotal: round2Decimals(subtotal),
            Impuestos: round2Decimals(impuestos),
            Total: round2Decimals(total),
            Requerido: i.requerido ? "Sí" : "No",
          };
        });
        filas.push({
          Código: "",
          Insumo: "",
          Cantidad: "",
          "Precio Unitario": "TOTAL",
          Subtotal: subtotalLista,
          Impuestos: impuestosLista,
          Total: totalLista,
          Requerido: "",
        });

        const hoja = XLSX.utils.json_to_sheet(filas, {
          header: [
            "Código",
            "Insumo",
            "Cantidad",
            "Precio Unitario",
            "Subtotal",
            "Impuestos",
            "Total",
            "Requerido",
          ],
        });
        hoja["!cols"] = [
          { wch: 10 },
          { wch: 45 },
          { wch: 10 },
          { wch: 14 },
          { wch: 14 },
          { wch: 14 },
          { wch: 14 },
          { wch: 12 },
        ];
        XLSX.utils.book_append_sheet(
          wb,
          hoja,
          this.sanitizarNombreHoja(nombreLista, usados)
        );

        resumen.push({
          Lista: nombreLista,
          "# Insumos": insumos.length,
          "# Requeridos": requeridos,
          Subtotal: subtotalLista,
          Impuestos: impuestosLista,
          Total: totalLista,
        });
      });

      // Hoja Resumen al inicio.
      const hojaResumen = XLSX.utils.json_to_sheet(resumen, {
        header: [
          "Lista",
          "# Insumos",
          "# Requeridos",
          "Subtotal",
          "Impuestos",
          "Total",
        ],
      });
      hojaResumen["!cols"] = [
        { wch: 45 },
        { wch: 12 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
      ];
      XLSX.utils.book_append_sheet(wb, hojaResumen, "Resumen");
      // Mover "Resumen" al frente.
      wb.SheetNames = [
        "Resumen",
        ...wb.SheetNames.filter((n) => n !== "Resumen"),
      ];

      XLSX.writeFile(wb, "Listas_Escuela.xlsx");
      message.success("Excel generado");
    } catch (err) {
      console.error(err);
      message.error("Ocurrió un error al generar el Excel");
    } finally {
      this.setState({ descargando: false });
    }
  };

  render() {
    const { todasListas, busqueda, seleccionadas } = this.state;

    const filtradas = busqueda
      ? todasListas.filter((l) =>
          (l.nombre || "")
            .toLowerCase()
            .includes(busqueda.trim().toLowerCase())
        )
      : todasListas;

    const columns = [
      { title: "Nombre", dataIndex: "nombre", key: "nombre" },
      {
        title: "Insumos",
        dataIndex: "cantidadInsumos",
        key: "cantidadInsumos",
        align: "center",
        width: "18%",
        render: (v) => v ?? "—",
      },
      {
        title: "Precio Total",
        dataIndex: "precioTotal",
        key: "precioTotal",
        align: "right",
        width: "22%",
        render: (v) => dineroDisabledMask(Number(v || 0)),
      },
    ];

    return (
      <>
        <PageHeader
          title="Exportar Listas a Excel"
          onBack={() => this.props.history.goBack()}
          style={{
            border: "1px solid rgb(235, 237, 240)",
            backgroundColor: "white",
            marginBottom: 0,
          }}
        />
        <div
          style={{
            backgroundColor: "white",
            padding: 16,
            marginTop: 8,
            borderRadius: 10,
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Buscar lista por nombre"
                value={busqueda}
                onChange={(e) => this.setState({ busqueda: e.target.value })}
                style={{ width: 280 }}
              />
              {seleccionadas.length > 0 && (
                <Tag color="cyan">{seleccionadas.length} seleccionada(s)</Tag>
              )}
            </div>
            <ButtonItem
              type="primary"
              olivo
              className="botonAgregar"
              icon={<FileExcelOutlined />}
              loading={this.state.descargando}
              disabled={seleccionadas.length === 0}
              onClick={this.descargarExcel}
            >
              Descargar Excel
            </ButtonItem>
          </div>

          <Table
            rowKey={(record) => String(record._id)}
            size="small"
            loading={this.state.loading}
            dataSource={filtradas}
            columns={columns}
            pagination={{ pageSize: 50, showSizeChanger: true }}
            rowSelection={{
              selectedRowKeys: seleccionadas,
              onChange: (keys) => this.setState({ seleccionadas: keys }),
            }}
            onRow={(record) => ({
              onClick: () => {
                const id = String(record._id);
                this.setState((prev) => ({
                  seleccionadas: prev.seleccionadas.includes(id)
                    ? prev.seleccionadas.filter((k) => k !== id)
                    : [...prev.seleccionadas, id],
                }));
              },
            })}
          />
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ExportarListas);
